import { t } from '../i18n';
import { McpChatWidgetProps } from '../interfaces/mcpChatWidgetProps';
import { store } from '../state/store';
import type { ChatMessage, WidgetConfig } from '../types';

export interface WidgetHealthConfig {
  title?: string;
  greeting?: string;
  otpGreeting?: string;
  providerGreetings?: Record<string, string>;
  uploadEnabled?: boolean;
  otpEnabled?: boolean;
  language?: 'en' | 'he';
  authProvider?: string;
  allowedTools?: number[];
  dangerousTools?: number[];
  isActive?: boolean;
  voiceEnabled?: boolean;
  drivingModeEnabled?: boolean;
  chatDebugUsageEnabled?: boolean;
}

export interface WidgetPropConfigResult {
  partial: Partial<WidgetConfig>;
  locale?: 'en' | 'he';
}

export interface WidgetRemoteConfigResult {
  partial: Partial<WidgetConfig>;
  shouldUpdateGreeting: boolean;
  isInactive: boolean;
}

export function buildAssistantMessage(content: string, isGreeting = false): ChatMessage {
  return {
    id: Math.random().toString(36).substring(2) + Date.now().toString(36),
    role: 'assistant',
    content,
    timestamp: Date.now(),
    isGreeting,
  };
}

export function getGreetingContent(
  config = store.getState().config,
  auth = store.getState().auth,
): string {
  const isAuthenticated = auth.status === 'authenticated';
  const isAnonymousMode = config.auth === 'none' || config.authProvider === 'none';

  if (isAnonymousMode || isAuthenticated) {
    return config.greeting || t('greeting');
  }

  return config.providerGreetings?.[config.authProvider] || config.otpGreeting || t('authPrompt');
}

export function buildConfigFromProps(
  nextProps: McpChatWidgetProps,
  authProvider: WidgetConfig['authProvider'],
): WidgetPropConfigResult {
  const partial: Partial<WidgetConfig> = {
    title: nextProps.title || '',
    greeting: nextProps.greeting || '',
    otpGreeting: nextProps.otpGreeting || '',
    widgetId: nextProps.widgetId || null,
  };

  let locale: 'en' | 'he' | undefined;

  if (nextProps.primaryColor) partial.primaryColor = nextProps.primaryColor;

  if (nextProps.position === 'bottom-left' || nextProps.position === 'bottom-right') {
    partial.position = nextProps.position;
  }

  if (nextProps.language === 'en' || nextProps.language === 'he') {
    partial.language = nextProps.language;
    locale = nextProps.language;
  }

  if (nextProps.serverUrl) partial.serverUrl = nextProps.serverUrl;

  if (nextProps.auth === 'none' || nextProps.auth === 'required') {
    partial.auth = nextProps.auth;
  }

  if (nextProps.sessionTimeout) {
    partial.sessionTimeout = Math.max(1, parseInt(nextProps.sessionTimeout, 10) || 10);
  }

  if (nextProps.displayMode === 'floating' || nextProps.displayMode === 'fullscreen') {
    partial.displayMode = nextProps.displayMode;
  }

  if (authProvider !== 'none') {
    partial.auth = 'required';
  }

  return { partial, locale };
}

export function mergeRemoteConfig(
  remoteConfig: WidgetHealthConfig,
  props: Pick<McpChatWidgetProps, 'title' | 'greeting' | 'otpGreeting'>,
): WidgetRemoteConfigResult {
  const partial: Partial<WidgetConfig> = {};

  if (remoteConfig.title && !props.title) partial.title = remoteConfig.title;
  if (remoteConfig.greeting && !props.greeting) partial.greeting = remoteConfig.greeting;
  if (remoteConfig.otpGreeting && !props.otpGreeting) partial.otpGreeting = remoteConfig.otpGreeting;
  if (remoteConfig.providerGreetings) partial.providerGreetings = remoteConfig.providerGreetings;
  if (remoteConfig.language) partial.language = remoteConfig.language;
  if (Array.isArray(remoteConfig.allowedTools)) partial.allowedTools = remoteConfig.allowedTools;
  if (Array.isArray(remoteConfig.dangerousTools)) partial.dangerousTools = remoteConfig.dangerousTools;
  if (typeof remoteConfig.voiceEnabled === 'boolean') partial.voiceEnabled = remoteConfig.voiceEnabled;
  if (typeof remoteConfig.chatDebugUsageEnabled === 'boolean') partial.chatDebugUsageEnabled = remoteConfig.chatDebugUsageEnabled;
  if (typeof remoteConfig.drivingModeEnabled === 'boolean') {
    partial.drivingModeEnabled = remoteConfig.voiceEnabled === false
      ? false
      : remoteConfig.drivingModeEnabled;
  }

  if (partial.voiceEnabled === false) {
    partial.drivingModeEnabled = false;
  }

  return {
    partial,
    shouldUpdateGreeting: !!(
      partial.greeting ||
      partial.otpGreeting ||
      partial.providerGreetings
    ),
    isInactive: remoteConfig.isActive === false,
  };
}
